/**
 * MapSheet — replaces the Edit Display Order popup that a tap on a NavCtrl map circle
 * opened. It shows the tapped stop or transfer station over the Run map, with Waze and,
 * for a stop, Move to Order #.
 *
 * Single-pane: a sheet across the bottom of the map, 8px in from each side.
 * Split:       a 380px card 16px from the map's left.
 * Both sit 32px up, clear of Google's attribution, and leave 8px above them. A sheet
 * taller than that (phone landscape) scrolls its body. The map never resizes for it.
 */

import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Center,
  Group,
  Paper,
  Select,
  Stack,
  Text,
} from '@mantine/core';
import { IconBrandWaze, IconX } from '@tabler/icons-react';

import { lineColour } from './lineColour';
import { MAP_CLEAR_BAND, MAP_CONTROL_INSET, stopPosition } from './runMap';
import { openInWaze } from './waze';
import type { Ref } from 'react';
import type { LineColour } from './lineColour';
import type { RunsheetLine, Station } from '@/types';

const CARD_INSET = 16;
const CARD_WIDTH = 380;
const TOP_GAP = 8;
// Three options high, as in the customer detail, so the list fits a phone in landscape.
const OPTION_LIST_HEIGHT = 150;

const STATUS_LABELS: Record<LineColour, string> = {
  green: 'Completed',
  gray: 'No pickup',
  orange: 'Extra pickup',
  violet: 'Install',
  blue: 'Pending',
};

/** What the sheet shows: a stop, with whether it is NEXT, or a transfer station. */
export type MapSheetItem =
  { kind: 'stop'; line: RunsheetLine; next: boolean } | { kind: 'station'; station: Station };

interface Props {
  item: MapSheetItem;
  split: boolean;
  orderOptions: string[];
  onMove: (line: RunsheetLine, displayorder: number) => void;
  onClose: () => void;
  ref?: Ref<HTMLDivElement>;
}

export function MapSheet({ item, split, orderOptions, onMove, onClose, ref }: Props) {
  const place = item.kind === 'stop' ? item.line : item.station;

  return (
    <Paper
      ref={ref}
      data-testid="map-sheet"
      shadow="xl"
      radius="md"
      pos="absolute"
      bottom={MAP_CLEAR_BAND}
      left={split ? CARD_INSET : MAP_CONTROL_INSET}
      right={split ? undefined : MAP_CONTROL_INSET}
      w={split ? CARD_WIDTH : undefined}
      mah={`calc(100% - ${MAP_CLEAR_BAND + TOP_GAP}px)`}
      style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      <Group gap="sm" wrap="nowrap" align="flex-start" p="md" pb="xs" style={{ flexShrink: 0 }}>
        {item.kind === 'stop' ? (
          <StopHeading line={item.line} next={item.next} />
        ) : (
          <Box flex={1} miw={0}>
            <Text fw={700}>{item.station.name}</Text>
            <Text fz="sm" c="dimmed">
              Transfer station
            </Text>
          </Box>
        )}
        <ActionIcon variant="subtle" color="gray" aria-label="Close" onClick={onClose}>
          <IconX size={20} />
        </ActionIcon>
      </Group>

      {/* The body scrolls as a whole, so its actions keep their 50px when the sheet is capped. */}
      <Box style={{ overflowY: 'auto', minHeight: 0 }}>
        <Stack gap="sm" px="md" pb="md">
          {item.kind === 'stop' && (
            <Select
              label="Move to Order #"
              data={orderOptions}
              value={item.line.displayorder > 0 ? String(item.line.displayorder) : null}
              placeholder="Not in the run yet"
              onChange={value => value && onMove(item.line, Number(value))}
              allowDeselect={false}
              maxDropdownHeight={OPTION_LIST_HEIGHT}
            />
          )}
          {/* A stop not on the map has no coordinates to send Waze to. */}
          {stopPosition(place) && (
            <Button
              fullWidth
              color="blue"
              leftSection={<IconBrandWaze size={20} />}
              onClick={() => openInWaze(place)}
            >
              Waze
            </Button>
          )}
        </Stack>
      </Box>
    </Paper>
  );
}

// ── Small sub-components ──────────────────────────────────────────────────────

function StopHeading({ line, next }: { line: RunsheetLine; next: boolean }) {
  const colour = lineColour(line);
  return (
    <>
      <Center
        w={44}
        h={44}
        bg={`${colour}.8`}
        c="white"
        fz={18}
        fw={800}
        style={{ flexShrink: 0, borderRadius: '50%' }}
      >
        {line.displayorder > 0 ? line.displayorder : '+'}
      </Center>
      <Box flex={1} miw={0}>
        <Text fw={700} lh={1.3}>
          {line.deliveryaddress}
        </Text>
        <Text fz="sm" c="dimmed" truncate>
          {line.namewithid}
        </Text>
        <Group gap={6} mt={4}>
          <Badge color={colour} variant="light" size="lg">
            {STATUS_LABELS[colour]}
          </Badge>
          {next && (
            <Badge color="dark" size="lg">
              Next
            </Badge>
          )}
          <Text fz="sm">
            {line.num_bags} {line.num_bags === 1 ? 'bag' : 'bags'}
          </Text>
        </Group>
      </Box>
    </>
  );
}
